<div class="dashboard-wrapper">
  <!-- Top Header Section -->
  <header class="dashboard-header">
    <div class="header-left">
      <div class="header-badge">
        <span class="pulse-dot"></span>
        <span>Platform Telemetry</span>
      </div>
      <h1 class="dashboard-title">Dashboard Overview</h1>
      <p class="dashboard-subtitle">Real-time performance analytics, session trends, and platform metrics</p>
    </div>

    <div class="header-actions">
      <!-- Refresh Button -->
      <button 
        mat-icon-button 
        class="refresh-btn" 
        [class.spinning]="isRefreshing"
        (click)="refreshDashboard()" 
        matTooltip="Refresh Metrics"
        [disabled]="isRefreshing || statsLoading || chartLoading">
        <mat-icon>refresh</mat-icon>
      </button>

      <!-- Download Report Button -->
      <button 
        mat-flat-button 
        (click)="downloadReport()" 
        [disabled]="downloading"
        class="download-btn">
        <mat-spinner *ngIf="downloading" diameter="18" class="btn-spinner"></mat-spinner>
        <mat-icon *ngIf="!downloading">file_download</mat-icon>
        <span>{{ downloading ? 'Exporting Report...' : 'Export User Report' }}</span>
      </button>
    </div>
  </header>

  <!-- Top Metrics Cards Grid -->
  <section class="metrics-grid">
    <!-- Total Users Card -->
    <div class="metric-card metric-users" [class.loading]="statsLoading">
      <div class="metric-glow"></div>
      <div class="metric-body">
        <div class="metric-header-row">
          <span class="metric-tag">Athletes</span>
          <div class="metric-icon-wrap users-icon">
            <mat-icon>groups</mat-icon>
          </div>
        </div>

        <div class="metric-content">
          <h2 class="metric-value">
            <ng-container *ngIf="!statsLoading; else skeletonValue">
              {{ totalUsers | number }}
            </ng-container>
          </h2>
          <span class="metric-label">Total Users</span>
        </div>

        <div class="metric-footer">
          <mat-icon class="footer-icon">verified_user</mat-icon>
          <span>Registered athlete accounts</span>
        </div>
      </div>
    </div>

    <!-- Total Profiles Card -->
    <div class="metric-card metric-profiles" [class.loading]="statsLoading">
      <div class="metric-glow"></div>
      <div class="metric-body">
        <div class="metric-header-row">
          <span class="metric-tag">Profiles</span>
          <div class="metric-icon-wrap profiles-icon">
            <mat-icon>badge</mat-icon>
          </div>
        </div>

        <div class="metric-content">
          <h2 class="metric-value">
            <ng-container *ngIf="!statsLoading; else skeletonValue">
              {{ totalProfiles | number }}
            </ng-container>
          </h2>
          <span class="metric-label">Total Profiles</span>
        </div>

        <div class="metric-footer">
          <mat-icon class="footer-icon">sports_mma</mat-icon>
          <span>Personalized boxer profiles</span>
        </div>
      </div>
    </div>

    <!-- Total Videos Card -->
    <div class="metric-card metric-videos" [class.loading]="statsLoading">
      <div class="metric-glow"></div>
      <div class="metric-body">
        <div class="metric-header-row">
          <span class="metric-tag">Sessions</span>
          <div class="metric-icon-wrap videos-icon">
            <mat-icon>videocam</mat-icon>
          </div>
        </div>

        <div class="metric-content">
          <h2 class="metric-value">
            <ng-container *ngIf="!statsLoading; else skeletonValue">
              {{ totalVideos | number }}
            </ng-container>
          </h2>
          <span class="metric-label">Total Recorded Videos</span>
        </div>

        <div class="metric-footer">
          <mat-icon class="footer-icon">play_circle</mat-icon>
          <span>Uploaded training rounds</span>
        </div>
      </div>
    </div>
  </section>

  <!-- Skeleton Template for Values -->
  <ng-template #skeletonValue>
    <div class="skeleton-pill"></div>
  </ng-template>

  <!-- Analytics Chart Section -->
  <section class="analytics-section">
    <div class="analytics-card">
      <div class="analytics-header">
        <div class="analytics-title-group">
          <div class="title-with-icon">
            <mat-icon class="section-icon">insights</mat-icon>
            <div>
              <h2 class="analytics-title">Punching Session Activity</h2>
              <p class="analytics-subtitle">Track workout frequency and session density over time</p>
            </div>
          </div>
        </div>

        <!-- Filter Segmented Controls -->
        <div class="filter-pills">
          <button 
            *ngFor="let filter of filters" 
            type="button"
            class="filter-pill"
            [class.active]="selectedFilter === filter.value"
            (click)="onFilterChange(filter.value)"
            [disabled]="chartLoading">
            <span class="full-text">{{ filter.label }}</span>
            <span class="short-text">{{ filter.shortLabel }}</span>
          </button>
        </div>
      </div>

      <!-- Quick Summary Stats Bar for Active Period -->
      <div class="summary-ribbon" *ngIf="chartData.length > 0 && !chartLoading">
        <div class="summary-item">
          <span class="summary-label">Total Sessions in Period</span>
          <span class="summary-value">{{ totalSessionsInPeriod | number }}</span>
        </div>
        <div class="summary-divider"></div>
        <div class="summary-item">
          <span class="summary-label">Average / Period</span>
          <span class="summary-value">{{ avgSessionsInPeriod }}</span>
        </div>
        <div class="summary-divider"></div>
        <div class="summary-item">
          <span class="summary-label">Peak Activity</span>
          <span class="summary-value highlight-peak">{{ peakSessionCount }} ({{ peakSessionLabel }})</span>
        </div>
      </div>

      <!-- Active Hover Insight Tooltip Banner -->
      <div class="active-insight-banner" [class.visible]="activeHoverItem !== null">
        <div class="insight-content" *ngIf="activeHoverItem">
          <mat-icon class="insight-icon">timeline</mat-icon>
          <span>
            <strong>{{ activeHoverItem.label }}</strong>: 
            <span class="highlight-val">{{ activeHoverItem.count }} sessions recorded</span>
            <span class="percentage-val" *ngIf="maxCount > 0">
              ({{ (activeHoverItem.count / maxCount * 100) | number:'1.0-0' }}% of peak)
            </span>
          </span>
        </div>
      </div>

      <!-- Chart Body -->
      <div class="chart-content-area">
        <!-- Loading State -->
        <div *ngIf="chartLoading" class="chart-loader-container">
          <mat-spinner diameter="40" class="chart-spinner"></mat-spinner>
          <p>Analyzing punching sessions...</p>
        </div>

        <!-- Data Available View -->
        <div *ngIf="!chartLoading && chartData.length > 0" class="chart-scroll-wrapper">
          <div class="chart-grid-container">
            <!-- Background Grid Lines -->
            <div class="chart-grid-lines">
              <div class="grid-line line-100"><span class="grid-label">{{ maxCount }}</span></div>
              <div class="grid-line line-50"><span class="grid-label">{{ (maxCount / 2) | number:'1.0-0' }}</span></div>
              <div class="grid-line line-0"><span class="grid-label">0</span></div>
            </div>

            <!-- Bar Chart Display -->
            <div class="chart-bars-track">
              <div 
                *ngFor="let item of chartData; let i = index" 
                class="chart-bar-column"
                (mouseenter)="onHoverItem(item)"
                (mouseleave)="onHoverItem(null)"
                [class.is-hovered]="activeHoverItem === item"
                [class.is-peak]="item.count === peakSessionCount && peakSessionCount > 0">
                
                <div class="bar-slot">
                  <!-- Bar floating count -->
                  <div class="bar-count-badge" [class.show]="item.count > 0">
                    {{ item.count }}
                  </div>

                  <!-- Actual Bar Pillar -->
                  <div 
                    class="bar-pillar" 
                    [style.height.%]="getBarHeight(item.count)">
                    <div class="bar-shine"></div>
                  </div>
                </div>

                <!-- Label underneath -->
                <div class="bar-axis-label" [title]="item.label">
                  {{ item.label }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div *ngIf="!chartLoading && chartData.length === 0" class="chart-empty-state">
          <div class="empty-icon-wrap">
            <mat-icon>bar_chart</mat-icon>
          </div>
          <h3>No Session Data Found</h3>
          <p>No punching video sessions recorded for this selected timeframe.</p>
          <button mat-stroked-button (click)="loadChartData()" class="empty-retry-btn">
            <mat-icon>refresh</mat-icon> Check Again
          </button>
        </div>
      </div>
    </div>
  </section>

  <!-- Quick Action Navigation Hub -->
  <section class="quick-actions-section">
    <div class="section-heading">
      <mat-icon class="heading-icon">bolt</mat-icon>
      <div>
        <h2 class="section-title">Quick Administration Hub</h2>
        <p class="section-subtitle">Direct shortcuts to manage athletes, leaderboards, and video media</p>
      </div>
    </div>

    <div class="action-cards-grid">
      <a 
        *ngFor="let action of quickActions" 
        [routerLink]="[action.route]"
        class="action-card" 
        [ngClass]="action.gradientClass">
        <div class="action-card-glow"></div>
        <div class="action-top">
          <div class="action-icon-circle">
            <mat-icon>{{ action.icon }}</mat-icon>
          </div>
          <span class="action-badge">{{ action.badgeText }}</span>
        </div>

        <div class="action-info">
          <h3 class="action-title">{{ action.title }}</h3>
          <p class="action-subtitle">{{ action.subtitle }}</p>
        </div>

        <div class="action-footer">
          <span class="action-cta">Access module</span>
          <mat-icon class="action-arrow">arrow_forward</mat-icon>
        </div>
      </a>
    </div>
  </section>
</div>
